<template>
  <BaseListRow class="tw-flex tw-relative tw-z-0">
    <div class="tw-grow">
      <WdsStack
        align-center
        justify-space-between
        :class="{
          'tw-mb-4':
            opened ||
            !latestState ||
            latestState.state === ApprovalApprovableState.Requested ||
            latestState?.state === ApprovalApprovableState.Rejected,
        }"
      >
        <WdsStack gap-xs align-start direction-column>
          <WdsStack align-center justify-center gap-sm class="tw-mb-1">
            <div>
              <WdsTag
                md
                :success="latestState?.state === ApprovalApprovableState.Approved"
                :error="
                  latestState?.state === ApprovalApprovableState.Rejected ||
                  latestState?.state === ApprovalApprovableState.Cancelled
                "
                :warning="!latestState || latestState.state === ApprovalApprovableState.Requested"
                class="!tw-rounded"
                :class="{
                  '!tw-bg-[#11A151] !tw-text-white':
                    latestState?.state === ApprovalApprovableState.Approved,
                  '!tw-bg-[#EB2E2E] !tw-text-white':
                    latestState?.state === ApprovalApprovableState.Rejected,
                  '!tw-bg-[#9999AA] !tw-text-white':
                    latestState?.state === ApprovalApprovableState.Cancelled,
                  '!tw-bg-[#FD8A33] !tw-text-white':
                    !latestState || latestState.state === ApprovalApprovableState.Requested,
                }"
                data-test="approval-request-status"
              >
                {{ status }}
              </WdsTag>
            </div>
            <WdsText xl>
              {{ approvable.approval.name }}
            </WdsText>
          </WdsStack>
          <WdsStack align-center justify-center gap-sm>
            <WdsText small>
              <WdsTag sm>{{ getApprovalTriggerLabel(approvable.approval.trigger) }}</WdsTag>
            </WdsText>
            <WdsText>
              <span class="!tw-text-gray-800 !tw-text-[0.8125rem]">
                Submitted {{ useDate(requestedBy?.createdAt, 'MMM DD, YYYY hh:mm').date.value }} by
                {{ requestedBy?.actionedBy.name }}
              </span>
            </WdsText>
          </WdsStack>
        </WdsStack>

        <WdsStack direction-column gap-sm class="tw-self-start tw-items-end tw-flex-shrink-0">
          <WdsStack gap-sm class="tw-items-center tw-justify-end">
            <WdsTag
              v-if="steps.length > 0"
              md
              gray
              class="!tw-bg-gray-100 !tw-rounded-full !tw-text-gray-800 tw-flex tw-items-center tw-gap-1"
              :title="
                $t('Steps approved: ') + currentApprovalStepNumber + ' / ' + countApprovalSteps
              "
            >
              <WdsStack align-center justify-center gap-xs>
                <WdsIcon name="checkmark-outline" :size="14" />
                <WdsText>
                  <span class="tw-font-normal">
                    {{ currentApprovalStepNumber }} / {{ countApprovalSteps }}
                  </span>
                </WdsText>
              </WdsStack>
            </WdsTag>
          </WdsStack>
          <WdsButtonIcon
            v-if="shouldShowToggleButton"
            sm
            text-left
            transparent-background
            class="tw-self-end"
            @click.prevent="opened = !opened"
          >
            <template #text>
              <span class="tw-text-base toggle-button">
                {{ opened ? $t('Collapse') : $t('See all') }}
              </span>
            </template>
            <WdsIconChevronDown
              :class="[
                'tw-transition-transform tw-duration-300 tw-transform tw-w-4 tw-h-4',
                opened ? 'tw-rotate-180' : '',
              ]"
            />
          </WdsButtonIcon>
        </WdsStack>
      </WdsStack>

      <WdsCollapse v-model:open="opened" content-id="approval-progress">
        <template #title>
          <span></span>
        </template>
        <template #content>
          <WdsProgress
            v-if="steps.length > 0"
            :steps="steps"
            :current="currentStep"
            :most-completed-step="mostCompletedStep"
            :failed-steps="failedSteps"
            secondary
            sm
            class="tw-w-full !tw-block"
          >
            <template #parent="{ mStep }">
              <ApprovalProgressStep
                :m-step="mStep"
                :viewer-can-action="approvable.viewerCanAction"
                @show-approval-request-modal="showApprovalRequestModal = true"
              />
            </template>
          </WdsProgress>
        </template>
      </WdsCollapse>

      <WdsProgress
        v-if="
          !opened &&
          steps.length > 0 &&
          latestState &&
          latestState.state === ApprovalApprovableState.Requested
        "
        :steps="[currentStepObject]"
        :current="[0]"
        secondary
        sm
        class="tw-w-full !tw-block"
      >
        <template #parent="{ mStep }">
          <ApprovalProgressStep
            :m-step="mStep"
            :viewer-can-action="approvable.viewerCanAction"
            @show-approval-request-modal="showApprovalRequestModal = true"
          />
        </template>
      </WdsProgress>

      <WdsProgress
        v-if="
          !opened &&
          steps.length > 0 &&
          latestState &&
          latestState.state === ApprovalApprovableState.Rejected
        "
        :steps="[rejectedStepObject]"
        :failed-steps="[0]"
        :most-completed-step="[1]"
        :current="[1]"
        secondary
        sm
        class="tw-w-full !tw-block"
      >
        <template #parent="{ mStep }">
          <ApprovalProgressStep
            :m-step="mStep"
            @show-approval-request-modal="showApprovalRequestModal = true"
          />
        </template>
      </WdsProgress>
    </div>
  </BaseListRow>

  <Teleport to="body">
    <ApprovalRequestModal
      v-model:show="showApprovalRequestModal"
      :approval-approvable-id="approvable.id"
      @refresh-approvables="refreshApprovalRequests"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue'
import {
  WdsTag,
  WdsText,
  WdsStack,
  WdsProgress,
  WdsCollapse,
  WdsButtonIcon,
  WdsIconChevronDown,
  WdsIcon,
} from '@worksome/wds-components'
import useDate from '@/composables/useDate'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import type { Approvable } from '@/components/Workflows/Approvals/approvals-types'
import { ApprovalApprovableState, ApprovalCancellationReason, ApprovalTrigger } from '@/gql/graphql'
import ApprovalRequestModal from '@/components/Workflows/Approvals/Request/ApprovalRequestModal.vue'
import BaseListRow from '@/components/BaseListRow.vue'
import ApprovalProgressStep from '@/components/Workflows/Approvals/Request/ApprovalProgressStep.vue'
import {
  getApprovalStateStatusLabel,
  getCancellationReasonLabel,
} from '@/components/Workflows/Approvals/utilities/statusFormatting'

const props = defineProps({
  approvable: {
    type: Object as PropType<Approvable>,
    required: true,
  },
  hideTalent: {
    type: Boolean,
    default: false,
  },
  progressListOnly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['reload-approvals'])

const approvers = computed(() => props.approvable?.approvalRule?.approvers || [])

const showApprovalRequestModal = ref(false)

const opened = ref(false)

const getApprovalTriggerLabel = (trigger: string) => {
  switch (trigger) {
    case ApprovalTrigger.HireCreated:
      return $t('Hire Created')
    case ApprovalTrigger.HireChanged:
      return $t('Hire Changed')
    case ApprovalTrigger.ClassificationCreated:
      return $t('Classification Created')
    default:
      return $t('Unknown')
  }
}

const requestedBy = computed(() =>
  props.approvable?.approvalStates?.find(
    (state) => state.state === ApprovalApprovableState.Requested,
  ),
)

const latestState = computed(() => {
  if (props.approvable?.approvalStates?.length > 0) {
    return props.approvable.approvalStates[props.approvable.approvalStates.length - 1]
  }

  return null
})

const status = computed(() => {
  if (!latestState.value) {
    return $t('Pending')
  }

  return getApprovalStateStatusLabel(latestState.value.state)
})

const steps = computed(() => {
  let stepNumber = 0
  const results: any = []
  const isOverallCancelled = latestState.value?.state === ApprovalApprovableState.Cancelled

  if (approvers.value.length > 0) {
    approvers.value.forEach((approver: any) => {
      const approverLatestState =
        props.approvable?.approvalStates
          ?.filter((state) => state.approver.id === approver.id)
          ?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ||
        null

      let isCurrent: boolean = approverLatestState?.state === ApprovalApprovableState.Requested
      const upComing: boolean = approverLatestState === null

      const user = !upComing && !isCurrent ? (approverLatestState?.actionedBy ?? null) : null
      const date = approverLatestState
        ? useDate(approverLatestState.createdAt, 'MMM DD, YYYY hh:mm').date.value
        : null
      const stepStatus = approverLatestState?.state
        ? {
            state: approverLatestState.state,
            label: getApprovalStateStatusLabel(approverLatestState.state),
          }
        : null

      const message = approverLatestState?.message ?? null

      if (approverLatestState?.state === ApprovalApprovableState.Cancelled) {
        isCurrent = false

        const cancellationReason = (() => {
          if (
            approverLatestState?.cancellationReason === ApprovalCancellationReason.ApprovableRemoved
          ) {
            return $t('The hire was removed')
          }

          const label = getCancellationReasonLabel(approverLatestState?.cancellationReason)

          const defaultMessage = $t('Request automatically cancelled')
          return label ? `${label} - ${defaultMessage}` : defaultMessage
        })()

        const cancellationDate = approverLatestState
          ? useDate(approverLatestState.createdAt, 'MMM DD, YYYY hh:mm').date.value
          : null

        results.push({
          name: cancellationReason ?? '',
          useTagForName: false,
          actionable: false,
          url: '',
          isCurrent,
          params: {
            user,
            date: cancellationDate,
            status: stepStatus,
            message,
            userGroup: approver.userGroup,
            upComing,
          },
        })
      }

      stepNumber += 1

      results.push({
        name: approver.userGroup.name,
        useTagForName: true,
        actionable: true,
        stepNumber,
        url: '',
        isCurrent,
        params: {
          user: stepStatus?.state === ApprovalApprovableState.Cancelled ? null : user,
          date,
          status: stepStatus?.state === ApprovalApprovableState.Cancelled ? null : stepStatus,
          message,
          userGroup:
            isOverallCancelled || stepStatus?.state === ApprovalApprovableState.Cancelled
              ? null
              : approver?.userGroup,
          upComing: !isCurrent,
        },
      })
    })
  }

  return results
})

const approvalSteps = computed(() =>
  steps.value.filter((step: { actionable: boolean }) => step.actionable),
)

const countApprovalSteps = computed(() => approvalSteps.value.length)

const currentApprovalStepNumber = computed(() => {
  // If all steps are approved, return the total count
  if (latestState.value?.state === ApprovalApprovableState.Approved) {
    return approvalSteps.value.length
  }

  const approvedSteps = steps.value.filter(
    (step: { params: { status: { state: string } | null } }) =>
      step.params.status?.state === ApprovalApprovableState.Approved,
  )

  return approvedSteps.length
})

const currentStep = computed(() => {
  const theCurrentStep = steps.value.findIndex((step: any) => step.isCurrent)

  if (theCurrentStep === -1) {
    return []
  }

  return [theCurrentStep]
})

const currentStepObject = computed(() => {
  const theCurrentStep = steps.value.findIndex((step: any) => step.isCurrent)

  if (theCurrentStep !== -1) {
    return steps.value[theCurrentStep]
  }

  return null
})

const rejectedStepObject = computed(() => {
  const theRejectedStep = steps.value.findIndex(
    (step: any) => step.params.status?.state === ApprovalApprovableState.Rejected,
  )

  if (theRejectedStep !== -1) {
    return steps.value[theRejectedStep]
  }

  return null
})

const mostCompletedStep = computed(() => {
  const theCurrentStep = steps.value.findIndex((step: any) => step.isCurrent)

  if (theCurrentStep !== -1) {
    return [theCurrentStep - 1]
  }

  if (latestState.value?.state === ApprovalApprovableState.Approved) {
    return [steps.value.length - 1]
  }

  return [0]
})

const failedSteps = computed(() => {
  if (
    latestState.value?.state === ApprovalApprovableState.Rejected ||
    latestState.value?.state === ApprovalApprovableState.Cancelled
  ) {
    return [
      steps.value?.findIndex(
        (step: any) =>
          step.params.status?.state === ApprovalApprovableState.Rejected ||
          step.params.status?.state === ApprovalApprovableState.Cancelled,
      ),
    ]
  }

  return []
})

const shouldShowToggleButton = computed(() => {
  const isPending =
    !latestState.value || latestState.value.state === ApprovalApprovableState.Requested
  const hasSingleStep = countApprovalSteps.value === 1

  return !(isPending && hasSingleStep)
})

function refreshApprovalRequests() {
  emit('reload-approvals')
}
</script>

<style scoped>
:deep(.wds-button-text) {
  margin-right: 4px !important;
}
</style>
